/**
 * 組織レポート v3 の文言引き当て。
 *
 * 正典: apps/web/docs/新方針20260821/GB組織_説明文DB_v1_23.json
 *       （src/features/analysis/data/orgReportTextsV1_23.json に複製）
 *
 * 【DB化について】
 *  個人レポート v2 と同じ方針。最終的にはDBへ格納するが、まず画面を固めるため
 *  JSONを直接読む。参照側は必ずこのモジュール経由にして、loadOrgTexts() の中身を
 *  API取得へ差し替えるだけで済むようにする。（14ブロック207件と小さい）
 *
 * 【ブロックの性格】
 *  entries には「表示する文言」と「実装の決めごと(RULE)」が混在している。
 *  id に -RULE- を含むものは画面に出さない。getRule() で参照するだけ。
 *
 * 【現行の orgArchetypes.ts（12分類CSV）との関係】
 *  12分類は旧路線の文言データで、この v3 では使わない。現行はそのまま温存する。
 */
import raw from '../data/orgReportTextsV1_23.json';
import type { AxisKey } from './gb74Report';
import type { Grade, ScoreKey, ConnectionVarsV3 } from './orgScoreV3';
import type { OrgShape } from './orgTypeV3';
import { SHAPE_DB_ID, SHAPE_TEXT_EXTRA } from './orgShapeCopyV3';

/** 文言DBの1エントリ。ブロックによって持つ項目が違う。 */
export interface OrgTextEntry {
  id: string;
  条件: string;
  見出し?: string;
  状態?: string;
  形状?: string;
  本文?: string;
  見るところ?: string;
  次の一手?: string;
  テーマ?: string;
  取り組み?: string;
  理由?: string;
  指標?: string;
  寄与?: string;
}

interface OrgTextBlock {
  name: string;
  判定入力?: string;
  出力?: string;
  entries: OrgTextEntry[];
}

type OrgTextDb = Record<string, OrgTextBlock>;

const DB = raw as unknown as OrgTextDb;

/**
 * 文言DB全体を取得する。DB化したらここをAPI取得（＋キャッシュ）に差し替える。
 */
export function loadOrgTexts(): OrgTextDb {
  return DB;
}

/** ブロックとIDを指定して1件引く。 */
export function findOrgText(block: string, id: string): OrgTextEntry | undefined {
  return DB[block]?.entries.find((e) => e.id === id);
}

/** 実装の決めごと(RULE)を引く。画面には出さない。 */
export function getRule(block: string, id: string): OrgTextEntry | undefined {
  return findOrgText(block, id);
}

// ===== DB01 組織の総合評価キャッチ =====

/**
 * 象限比率のレンジがこれ未満なら平坦型として扱う。
 *
 * 4象限の比率は合計1なので、レンジ0.15未満は「4つとも15ポイント以内に収まっている」
 * ＝どの方向にも寄っていない状態。分散型の判定（EVEN_MAX_GAP）と同じ幅を使う。
 */
export const FLAT_RATE_RANGE_MAX = 0.15;

/** 平坦型のときに使う枝。 */
export type FlatBranch = '均質' | '二極' | '保留-低回答' | '保留-分極不能';

export interface OrgCatchInput {
  /**
   * 象限分布の比率（寄っている人の中での割合。合計1）。
   *
   * 【なぜ4軸平均ではなく象限比率か（2026-08-22）】
   *  4軸平均は全員の点数をならした値で、優劣がほとんど出ない（レンジ1〜2点）。
   *  そのため「平均の最高軸」と「型を決めた象限」が食い違い、
   *  例: キャッチが「A（整える力）が強い」なのに型はCD、ということが起きていた。
   *  型・分布・キャッチをすべて象限分布から出すことで、必ず同じ話になるようにする。
   */
  rates: Record<AxisKey, number>;
  /** 方向が定まらない型（中心集中型・分散型）か。真なら平坦型の枝に入る */
  directionless?: boolean;
  /** 分極が高いか（DB14の二極型判定と同じ入力を使う） */
  polarHigh: boolean;
  /** 分極を判定できるだけの人数があるか */
  canJudgePolar: boolean;
  /** 回答が集まりきっていないか（回答率が低い） */
  lowResponse?: boolean;
}

/**
 * DB01: 最高軸 × 最低軸 のキャッチ。レンジが小さいときは平坦型の枝に入る。
 *
 * 【なぜペアで1文にするか（10-RULE-ペア）】
 *  4軸の乖離の合計は必ず0になる。1軸が厚ければ必ずどこかが同じだけ薄い。
 *  軸ごとに独立した文を出すと、どの会社も同じ構造の文章になってしまう。
 */
export function getOrgCatch(input: OrgCatchInput): OrgTextEntry | undefined {
  const { rates, directionless, polarHigh, canJudgePolar, lowResponse } = input;
  const keys: AxisKey[] = ['A', 'B', 'C', 'D'];
  const vals = keys.map((k) => rates[k]);
  const range = Math.max(...vals) - Math.min(...vals);

  if (directionless || range < FLAT_RATE_RANGE_MAX) {
    const branch: FlatBranch = lowResponse
      ? '保留-低回答'
      : !canJudgePolar
        ? '保留-分極不能'
        : polarHigh
          ? '二極'
          : '均質';
    return findOrgText('DB01', `01-FLAT-${branch}`);
  }

  // 同値のときは A→B→C→D の順で先に来るものを採る（表示順と揃える）
  const high = keys.reduce((a, b) => (rates[a] >= rates[b] ? a : b));
  const low = keys.reduce((a, b) => (rates[a] <= rates[b] ? a : b));
  return findOrgText('DB01', `01-${high}${low}`);
}

/**
 * DB01の断定を弱める必要があるか（01-RULE-暫定語気）。
 *
 * O帯（組織の軸強度帯）の判定を保留している間、ベース文は向きだけを示す表現に
 * 置き換える決まりになっている。ただし語尾の機械置換は文が壊れるため行わない
 * （個人レポートで「書き出してもらう」→「書き出しる」の崩れが出た）。
 * 画面側で注記を添える形にし、置換の要否だけをここで返す。
 */
export const NEEDS_TENTATIVE_TONE = true;

// ===== DB02 3スコアの説明文 =====

const SCORE_ID: Record<ScoreKey, string> = {
  wellbeing: '働',
  connection: '繋',
  retention: '離',
};

/** DB02: スコア種別 × グレード の説明文。 */
export function getScoreText(key: ScoreKey, grade: Grade): OrgTextEntry | undefined {
  return findOrgText('DB02', `02-${SCORE_ID[key]}-${grade}`);
}

// ===== DB06 つながりの内訳 =====

/**
 * つながり4変数と文言DBのID断片の対応。
 * DBは旧称（三角/V/属人/橋渡）で書かれているため、ここで対応づける。
 */
const CONN_ID: Record<keyof ConnectionVarsV3, string> = {
  teamHelp: '三角',
  consultReach: 'V',
  workSharing: '属人',
  crossDept: '橋渡',
};

/** DB06: つながり変数 × グレード の1〜2文。 */
export function getConnVarText(
  key: keyof ConnectionVarsV3,
  grade: Grade
): OrgTextEntry | undefined {
  return findOrgText('DB06', `06-${CONN_ID[key]}-${grade}`);
}

// ===== DB14 GB組織タイプ =====

/** 軸の表示名（文言の組み立てに使う）。 */
const AXIS_NAME: Record<AxisKey, string> = {
  A: 'A 論理',
  B: 'B 着実',
  C: 'C 共感',
  D: 'D 創造',
};

/**
 * DB14: タイプコードの説明。
 * 1軸(A/B/C/D)と2軸(AC/CA/…)は個別に持つ。均等型は 14-均等。
 *
 * 【3軸のとき】DB14 の 14-3軸 は本文の中に生成の指示が書かれている。
 *   「並び順の先頭2つを文中で先に触れ、3つ目は『加えて』で添えます。
 *     24通りすべてに個別の文言は用意せず、上位2軸の組で選び、3軸目を添える形で生成します」
 * この指示どおりに組み立てる。以前は指示文をそのままレポートに出していた。
 */
export function getOrgTypeText(code: string): OrgTextEntry | undefined {
  if (code === '全方') return findOrgText('DB14', '14-均等');
  const direct = findOrgText('DB14', `14-${code}`);
  if (direct) return direct;

  if (code.length === 3) {
    const top2 = findOrgText('DB14', `14-${code.slice(0, 2)}`);
    const third = code[2] as AxisKey;
    if (top2?.本文) {
      return {
        id: `14-3軸-${code}`,
        条件: `3軸：${code}`,
        本文: `${top2.本文}加えて${AXIS_NAME[third]}の持ち味も広く見られます。`,
      };
    }
  }
  // ここに来るのは想定外の並び。指示文が出ないよう、汎用の1文にする。
  return {
    id: '14-3軸',
    条件: '3軸が平均優勢率を上回る',
    本文: '3つの持ち味が広く分かれている会社です。',
  };
}

/**
 * DB14: 型の説明。人数が足りず判定できないときは 14-型-判定不可。
 *
 * DB14 は 二極/集中/分散/中間 の4つしか持たない。象限分布による統一判定で
 * 加わった「同系統型」「中心集中型」は orgShapeCopyV3 の文言を使う
 * （DB14 に 14-型-同系統 等が追加されたら、そちらが自動的に優先される）。
 */
export function getOrgShapeText(shape: OrgShape | null): OrgTextEntry | undefined {
  if (!shape) return findOrgText('DB14', '14-型-判定不可');
  const dbId = SHAPE_DB_ID[shape];
  if (dbId) {
    const fromDb = findOrgText('DB14', dbId);
    if (fromDb) return fromDb;
  }
  const extra = SHAPE_TEXT_EXTRA[shape];
  if (extra) {
    const fromDb = findOrgText('DB14', extra.id);
    if (fromDb) return fromDb;
    return { id: extra.id, 条件: 'DB14に該当区分が無いため補った型', 本文: extra.本文 };
  }
  return undefined;
}

// ===== DB10 偶然の範囲の注記 =====

/**
 * 4軸平均の傾きが「偶然の範囲」とみなされる乖離の閾値 T(n)（10-基準表）。
 * 判定には使わず、注記を出すかどうかにだけ使う。
 */
export function axisTiltNoteThreshold(n: number): number {
  if (n < 15) return Infinity; // 15名未満は注記の判断もしない
  if (n <= 25) return 1.3;
  if (n <= 40) return 1.1;
  if (n <= 70) return 0.9;
  return 0.7;
}

/**
 * 4軸平均の傾きが偶然の範囲かを判定し、必要なら注記(10-注記-偶然)を返す。
 * 最高軸と最低軸の 6.0 からの乖離が両方とも閾値未満のとき注記する。
 */
export function getAxisTiltNote(
  axisMeans: Record<AxisKey, number>,
  memberCount: number
): OrgTextEntry | undefined {
  const keys: AxisKey[] = ['A', 'B', 'C', 'D'];
  const dev = keys.map((k) => axisMeans[k] - 6.0);
  const maxPos = Math.max(...dev);
  const maxNeg = Math.abs(Math.min(...dev));
  const t = axisTiltNoteThreshold(memberCount);
  if (maxPos < t && maxNeg < t) return findOrgText('DB10', '10-注記-偶然');
  return undefined;
}

// ===== 共通ヘルパー =====

/** 4軸の組織平均（0〜12）。合計は常に24になる。 */
export function axisMeansOf(members: Array<Record<AxisKey, number>>): Record<AxisKey, number> {
  const keys: AxisKey[] = ['A', 'B', 'C', 'D'];
  const n = Math.max(1, members.length);
  const out = {} as Record<AxisKey, number>;
  keys.forEach((k) => {
    out[k] = members.reduce((s, m) => s + m[k], 0) / n;
  });
  return out;
}
